import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, day, useCurrentPartner } from '../../lib'
import { Empty, ErrorBox, Loading } from '../../app/ui'

type Suggestion = { external_customer_id: string; name: string | null; email: string | null; domain: string | null }
type Request = {
  id: string; company_name: string; billing_email: string | null; customer_ref: string | null; note: string | null
  status: string; via: string; created_at: string; suggestions?: Suggestion[]
}

/** /partner/link-requests: startups asking to be linked as your customer (pr/onboarding/links). */
export function LinkRequestsPage() {
  const partner = useCurrentPartner()
  const q = useQuery({
    queryKey: ['partner', 'link-requests', partner?.id],
    queryFn: () => api<{ rows: Request[] }>('GET', `/api/partners/${partner!.id}/link-requests`),
    enabled: !!partner,
  })
  if (!partner) return <div className="page narrow"><p className="muted">You are not on a partner team yet.</p></div>
  if (q.isLoading) return <Loading />
  if (q.error) return <ErrorBox error={q.error} />
  const pending = q.data!.rows.filter((r) => r.status === 'pending')
  const done = q.data!.rows.filter((r) => r.status !== 'pending')

  return (
    <div className="page stack" data-testid="link-requests">
      <div className="stack" style={{ gap: '0.5rem' }}>
        <h1>Link requests</h1>
      </div>
      {pending.length === 0 ? <Empty>Nothing waiting.</Empty> : pending.map((r) => <PendingRow key={r.id} r={r} />)}
      {done.length ? (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Company</th><th>Asked</th><th>Result</th></tr></thead>
            <tbody>
              {done.map((r) => (
                <tr key={r.id}><td>{r.company_name}</td><td>{day(r.created_at)}</td><td>{r.status}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}

function PendingRow({ r }: { r: Request }) {
  const qc = useQueryClient()
  const [ext, setExt] = useState(r.suggestions?.[0]?.external_customer_id ?? r.customer_ref ?? '')
  const act = useMutation({
    mutationFn: (confirm: boolean) =>
      confirm
        ? api('POST', `/api/link-requests/${r.id}/confirm`, { external_customer_id: ext.trim() })
        : api('POST', `/api/link-requests/${r.id}/decline`, { reason: 'No matching customer' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partner', 'link-requests'] }),
  })
  return (
    <section className="panel stack" data-testid={`link-request-${r.id}`}>
      <h2>{r.company_name}</h2>
      <dl className="facts">
        <dt>Billing email</dt><dd>{r.billing_email ?? 'not given'}</dd>
        {r.customer_ref ? (<><dt>Their account ID</dt><dd className="mono">{r.customer_ref}</dd></>) : null}
        {r.note ? (<><dt>Note</dt><dd>{r.note}</dd></>) : null}
        <dt>Asked</dt><dd>{day(r.created_at)}{r.via === 'agent' ? ' · through their agent' : ''}</dd>
      </dl>
      {r.suggestions?.length ? (
        <div className="chips">
          {r.suggestions.map((s) => (
            <button key={s.external_customer_id} type="button" aria-pressed={ext === s.external_customer_id}
              onClick={() => setExt(s.external_customer_id)}>
              {s.name ?? s.email ?? s.external_customer_id} · {s.external_customer_id}
            </button>
          ))}
        </div>
      ) : <p className="muted">No customer record of yours matched. Enter their customer ID from your billing system.</p>}
      <label>
        Your customer ID for them
        <input value={ext} onChange={(e) => setExt(e.target.value)} placeholder="cus_…" />
      </label>
      {act.error ? <ErrorBox error={act.error} /> : null}
      <div className="row">
        <button className="primary" type="button" disabled={!ext.trim() || act.isPending} onClick={() => act.mutate(true)}>Confirm customer</button>
        <button type="button" disabled={act.isPending} onClick={() => act.mutate(false)}>Not our customer</button>
      </div>
    </section>
  )
}
